/**
 * commitCustomerVehicleStep — step-1 "Next" persistence for the work-order /
 * quotation create wizard (#1020).
 *
 * The first step's Next button is the single primary action: a new person or
 * company draft and a new vehicle draft are validated and saved HERE, not via
 * separate save buttons and not at the final work-order submit. Order matters:
 * the orderer party is created first so a new vehicle can be linked to it.
 *
 * Idempotent retry: the `onPartyCreated` / `onVehicleCreated` callbacks fire
 * the moment each entity exists, and the caller uses them to update the wizard
 * state (orderer selected, vehicle resolved). If a later phase fails, the next
 * attempt starts from the already-updated state and only re-runs the missing
 * part — nothing is created twice.
 */
import type { TFunction } from 'i18next';
import {
  personToPartyRef,
  companyToPartyRef,
  type PartyRef,
} from '@/components/parties/PartyPicker';
import {
  personFieldsToPayload,
  validatePersonFields,
  type PersonFieldsData,
} from '@/components/persons/PersonFormFields';
import {
  companyFieldsToPayload,
  validateCompanyFields,
  type CompanyFieldsData,
} from '@/components/companies/CompanyFormFields';
import {
  validateVehicleFields,
  type VehicleFieldsData,
} from '@/components/vehicles/VehicleFormFields';
import type { Person } from '@/hooks/usePersons';
import type { BusinessIdMatch, Company } from '@/hooks/useCompanies';
import type { VehicleDetail } from '@/hooks/useVehicles';

type InlinePartyCreate = 'person' | 'company' | null;

// --- Validation (shared by canProceed and the Next handler) ---

interface ValidateArgs {
  inlinePartyCreate: InlinePartyCreate;
  personDraft: PersonFieldsData;
  companyDraft: CompanyFieldsData;
  isCreatingNewVehicle: boolean;
  newVehicleData: VehicleFieldsData;
  tPersons: TFunction;
  tCompanies: TFunction;
  tVehicles: TFunction;
}

export interface CustomerVehicleDraftValidation {
  personErrors: Partial<Record<keyof PersonFieldsData, string>>;
  companyErrors: Partial<Record<keyof CompanyFieldsData, string>>;
  vehicleErrors: Partial<Record<keyof VehicleFieldsData, string>>;
  valid: boolean;
}

export function validateCustomerVehicleDraft(args: ValidateArgs): CustomerVehicleDraftValidation {
  const personErrors =
    args.inlinePartyCreate === 'person' ? validatePersonFields(args.personDraft, args.tPersons) : {};
  const companyErrors =
    args.inlinePartyCreate === 'company' ? validateCompanyFields(args.companyDraft, args.tCompanies) : {};
  const vehicleErrors = args.isCreatingNewVehicle
    ? validateVehicleFields(args.newVehicleData, args.tVehicles)
    : {};
  return {
    personErrors,
    companyErrors,
    vehicleErrors,
    valid:
      Object.keys(personErrors).length === 0 &&
      Object.keys(companyErrors).length === 0 &&
      Object.keys(vehicleErrors).length === 0,
  };
}

// --- Commit ---

/** Vehicle create payload — structurally matches useCreateVehicle's input. */
export interface CreateVehiclePayload {
  owner: {
    partyType: 'person' | 'company';
    personId?: string | null | undefined;
    companyId?: string | null | undefined;
  };
  registrationNumber: string;
  make?: string | undefined;
  model?: string | undefined;
  year?: number | undefined;
  vin?: string | undefined;
  mileage?: number | undefined;
}

export type CommitPhase = 'person' | 'company' | 'vehicle';

export type CommitResult =
  | { ok: true }
  | { ok: false; phase: CommitPhase; error: unknown }
  | { ok: false; phase: 'company'; cancelled: true };

/** Operator choice when the drafted company reuses a business id (#2410). */
export type CompanyBusinessIdChoice =
  | { kind: 'useExisting'; company: Company }
  | { kind: 'createBranch' }
  | { kind: 'cancel' };

type CompanyCreatePayload = ReturnType<typeof companyFieldsToPayload> & {
  allowDuplicateBusinessId?: boolean;
};

interface CommitArgs {
  ordererParty: PartyRef | null;
  inlinePartyCreate: InlinePartyCreate;
  personDraft: PersonFieldsData;
  companyDraft: CompanyFieldsData;
  isCreatingNewVehicle: boolean;
  newVehicleData: VehicleFieldsData;
  createPerson: (data: ReturnType<typeof personFieldsToPayload>) => Promise<Person>;
  createCompany: (data: CompanyCreatePayload) => Promise<Company>;
  createVehicle: (data: CreateVehiclePayload) => Promise<VehicleDetail>;
  /**
   * When both are set, a shared business id is resolved before insert:
   * keep the company already on file, confirm a new branch, or cancel.
   * A failed lookup still tries the insert; a 409 then asks once more.
   */
  lookupCompaniesByBusinessId?: (input: {
    businessId: string;
    name: string;
  }) => Promise<{ matches: readonly BusinessIdMatch[] }>;
  resolveCompanyBusinessIdConflict?: (
    matches: readonly BusinessIdMatch[],
  ) => Promise<CompanyBusinessIdChoice>;
  /** Fired as soon as the party row exists — caller marks it as the orderer. */
  onPartyCreated: (party: PartyRef) => void;
  /** Fired as soon as the vehicle row exists — caller resolves it in the wizard. */
  onVehicleCreated: (vehicle: VehicleDetail, owner: PartyRef) => void;
}

function isHttpConflict(error: unknown): boolean {
  if (typeof error !== 'object' || error === null || !('status' in error)) return false;
  return (error as { status: unknown }).status === 409;
}

async function readBusinessIdMatches(
  lookup: NonNullable<CommitArgs['lookupCompaniesByBusinessId']>,
  businessId: string,
  name: string,
): Promise<readonly BusinessIdMatch[] | null> {
  try {
    const result = await lookup({ businessId, name });
    return result.matches;
  } catch {
    return null;
  }
}

export async function commitCustomerVehicleStep(args: CommitArgs): Promise<CommitResult> {
  let orderer = args.ordererParty;

  if (args.inlinePartyCreate === 'person') {
    try {
      const created = await args.createPerson(personFieldsToPayload(args.personDraft));
      orderer = personToPartyRef(created);
      args.onPartyCreated(orderer);
    } catch (error) {
      return { ok: false, phase: 'person', error };
    }
  } else if (args.inlinePartyCreate === 'company') {
    const payload = companyFieldsToPayload(args.companyDraft);
    const businessId = payload.businessId;
    const lookup = args.lookupCompaniesByBusinessId;
    const resolveConflict = args.resolveCompanyBusinessIdConflict;

    const adopt = (company: Company) => {
      orderer = companyToPartyRef(company);
      args.onPartyCreated(orderer);
    };
    const insertCompany = async (allowDuplicate: boolean) => {
      const created = await args.createCompany(
        allowDuplicate ? { ...payload, allowDuplicateBusinessId: true } : payload,
      );
      adopt(created);
    };
    const applyChoice = async (choice: CompanyBusinessIdChoice) => {
      if (choice.kind === 'cancel') return 'cancel' as const;
      if (choice.kind === 'useExisting') {
        adopt(choice.company);
        return 'done' as const;
      }
      await insertCompany(true);
      return 'done' as const;
    };

    try {
      if (businessId && lookup && resolveConflict) {
        const matches = await readBusinessIdMatches(lookup, businessId, payload.name);
        if (matches != null && matches.length > 0) {
          if ((await applyChoice(await resolveConflict(matches))) === 'cancel') {
            return { ok: false, phase: 'company', cancelled: true };
          }
        } else {
          try {
            await insertCompany(false);
          } catch (error) {
            // The lookup can miss a row that the insert then rejects. Ask once
            // more so a failed lookup does not leave the wizard on a bare 409.
            if (!isHttpConflict(error)) {
              return { ok: false, phase: 'company', error };
            }
            const again = await readBusinessIdMatches(lookup, businessId, payload.name);
            if (again == null || again.length === 0) {
              return { ok: false, phase: 'company', error };
            }
            if ((await applyChoice(await resolveConflict(again))) === 'cancel') {
              return { ok: false, phase: 'company', cancelled: true };
            }
          }
        }
      } else {
        await insertCompany(false);
      }
    } catch (error) {
      return { ok: false, phase: 'company', error };
    }
  }

  if (args.isCreatingNewVehicle) {
    if (!orderer) {
      // canProceed guards against this; kept as a hard stop for safety.
      return { ok: false, phase: 'vehicle', error: new Error('Orderer missing for new vehicle') };
    }
    try {
      const created = await args.createVehicle({
        owner: {
          partyType: orderer.partyType,
          personId: orderer.partyType === 'person' ? orderer.personId : undefined,
          companyId: orderer.partyType === 'company' ? orderer.companyId : undefined,
        },
        registrationNumber: args.newVehicleData.registrationNumber.trim(),
        make: args.newVehicleData.make.trim() || undefined,
        model: args.newVehicleData.model.trim() || undefined,
        year: args.newVehicleData.year ? Number(args.newVehicleData.year) : undefined,
        vin: args.newVehicleData.vin.trim() || undefined,
        mileage: args.newVehicleData.mileage ? Number(args.newVehicleData.mileage) : undefined,
      });
      args.onVehicleCreated(created, orderer);
    } catch (error) {
      return { ok: false, phase: 'vehicle', error };
    }
  }

  return { ok: true };
}
